import cloneDeep from 'lodash-es/cloneDeep';

/**
 * This Data Structure:
 * - get initial data in constructor and store its snapshot in a private property
 * - all changes will be recorded in a initial data reference
 * - when call
 */
export class MemorizedClass<T extends object, K extends keyof T = keyof T> {
	private readonly originalData: T;
	public data: T;

	constructor(initialData: T) {
		this.originalData = cloneDeep(initialData);
		this.data = initialData;
	}

	public getOriginal(): T {
		return this.originalData;
	}

	public getChanges(): Record<K, {oldValue: T[K]; newValue: T[K]}> {
		return compareObjects(this.originalData, this.data);
	}

	public getDiff(dataToCompare: T): Record<K, {oldValue: T[K]; newValue: T[K]}> {
		return compareObjects(this.data, dataToCompare);
	}
}

function compareObjects<T extends object, K extends keyof T = keyof T>(obj1: T, obj2: T): Record<K, {oldValue: T[K]; newValue: T[K]}> {
	const recordOfChanges: Record<K, {oldValue: T[K]; newValue: T[K]}> = {} as Record<K, {oldValue: T[K]; newValue: T[K]}>;

	function compareRecursively(obj1: any, obj2: any, path: string) {
		if (obj1 === obj2) return;

		if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
			recordOfChanges[path] = {oldValue: obj1, newValue: obj2};
			return;
		}

		for (const key in obj1) {
			if (!(key in obj2)) {
				recordOfChanges[path + '.' + key] = {oldValue: obj1[key], newValue: undefined};
			} else {
				compareRecursively(obj1[key], obj2[key], path + '.' + key);
			}
		}

		for (const key in obj2) {
			if (!(key in obj1)) {
				recordOfChanges[path + '.' + key] = {oldValue: undefined, newValue: obj2[key]};
			}
		}
	}

	compareRecursively(obj1, obj2, '');
	return recordOfChanges;
}
